import { BRAND_HEALTH_MULTI_BRAND_LEGACY } from '@/app/dashboards/utils/layouts/brand-health-multi-brand-legacy.enum';
import { BRAND_HEALTH_SINGLE_BRAND_LEGACY } from '@/app/dashboards/utils/layouts/brand-health-single-brand-legacy.enum';
import { FACEBOOK_OVERVIEW_MULTI_BRAND_LEGACY } from '@/app/dashboards/utils/layouts/facebook-overview-multi-brand-legacy.enum';
import { FACEBOOK_OVERVIEW_SINGLE_BRAND_LEGACY } from '@/app/dashboards/utils/layouts/facebook-overview-single-brand-legacy.enum';
import { LISTENING_OVERVIEW_MULTI_TOPIC } from '@/app/dashboards/utils/layouts/listening-overview-multi-topic.enum';
import { LISTENING_OVERVIEW_SINGLE_TOPIC } from '@/app/dashboards/utils/layouts/listening-overview-single-topic.enum';
import { getCdnAssetUrl, getSrcSet, IMG } from '@/assets';
import { PRODUCT_MAP } from '@/models/auth/customer-plan.enum';
import { BRAND, ORGANIZATION, USER } from '@/models/auth/permissions.enum';
import { useFlagStore } from '@/stores/flag';

import { CHILI_PIPER_FORMS_MAP } from '../../settings/constants';
import { BRAND_HEALTH_MULTI_BRAND } from './layouts/brand-health-multi-brand.enum';
import { BRAND_HEALTH_SINGLE_BRAND } from './layouts/brand-health-single-brand.enum';
import { COMMUNITY_MULTI_BRAND } from './layouts/community-multi-brand.enum';
import { COMMUNITY_SINGLE_BRAND } from './layouts/community-single-brand.enum';
import { COMPETITIVE_OVERVIEW_MULTI_BRAND } from './layouts/competitive-overview-multi-brand.enum';
import { CREATOR_OVERVIEW_MULTI_BRAND } from './layouts/creator-overview-multi-brand.enum';
import { CREATOR_OVERVIEW_MULTI_CREATOR } from './layouts/creator-overview-multi-creator-enum';
import { CREATOR_OVERVIEW_SINGLE_BRAND } from './layouts/creator-overview-single-brand.enum';
import { CREATOR_OVERVIEW_SINGLE_CREATOR } from './layouts/creator-overview-single-creator.enum';
import { FACEBOOK_OVERVIEW_MULTI_BRAND } from './layouts/facebook-overview-multi-brand.enum';
import { FACEBOOK_OVERVIEW_SINGLE_BRAND } from './layouts/facebook-overview-single-brand.enum';
import { INSTAGRAM_OVERVIEW_MULTI_BRAND } from './layouts/instagram-overview-multi-brand.enum';
import { INSTAGRAM_OVERVIEW_SINGLE_BRAND } from './layouts/instagram-overview-single-brand.enum';
import { LINKEDIN_OVERVIEW_MULTI_BRAND } from './layouts/linkedin-overview-multi-brand.enum';
import { LINKEDIN_OVERVIEW_SINGLE_BRAND } from './layouts/linkedin-overview-single-brand.enum';
import { PINTEREST_OVERVIEW_MULTI_BRAND } from './layouts/pinterest-overview-multi-brand.enum';
import { PINTEREST_OVERVIEW_SINGLE_BRAND } from './layouts/pinterest-overview-single-brand.enum';
import { SOCIAL_OVERVIEW_SINGLE_BRAND } from './layouts/social-overview-single-brand.enum';
import { TIKTOK_OVERVIEW_MULTI_BRAND } from './layouts/tiktok-overview-multi-brand.enum';
import { TIKTOK_OVERVIEW_SINGLE_BRAND } from './layouts/tiktok-overview-single-brand.enum';
import { TWITTER_OVERVIEW_MULTI_BRAND } from './layouts/twitter-overview-multi-brand.enum';
import { TWITTER_OVERVIEW_SINGLE_BRAND } from './layouts/twitter-overview-single-brand.enum';
import { YOUTUBE_OVERVIEW_MULTI_BRAND } from './layouts/youtube-overview-multi-brand.enum';
import { YOUTUBE_OVERVIEW_SINGLE_BRAND } from './layouts/youtube-overview-single-brand.enum';

export const TEMPLATE_UPGRADE_MAP = Object.freeze({
  brandHealth: {
    title: 'Available With <br/>Social Listening',
    description:
      'Check the pulse of your brand across channels with the Brand Health template. Available with the Social Listening add-on.',
    buttonNameOld: 'Start Free Trial',
    // #TODO: To Be Removed and renamed with feature flag removal ticket https://app.shortcut.com/dashhudson/story/120622/remove-feature-flag
    bannerButtonName: 'Try Social Listening',
    walnutTourLink:
      'https://www.dashhudson.com/interactive-demo/social-listening-product-demo?utm_medium=referral&utm_source=dh_platform&utm_campaign=sl_upgrade',
    chiliPiper: CHILI_PIPER_FORMS_MAP.socialListening.contact,
    product: PRODUCT_MAP.SOCIAL_LISTENING,
    popupName: 'startTrial',
  },
  competitiveOverview: {
    title: 'Competitive Reporting Available In Premium Analytics',
    description:
      'Easily benchmark and compare your performance against your competition with Competitive Dashboards. Available with Premium Analytics add-on.',
    buttonNameOld: 'Start Free Trial',
    // #TODO: To Be Removed and renamed with feature flag removal ticket https://app.shortcut.com/dashhudson/story/120622/remove-feature-flag
    bannerButtonName: 'Try Competitive',
    chiliPiper: CHILI_PIPER_FORMS_MAP.premiumAnalytics.competitors,
    product: PRODUCT_MAP.PREMIUM_ANALYTICS,
    popupName: 'startTrial',
  },
  communityOverview: {
    title: 'Community Reporting Available In Premium Analytics',
    description:
      'Easily benchmark and compare your performance against your competition with Community Dashboards. Available with Premium Analytics add-on.',
    buttonNameOld: 'Start Free Trial',
    bannerButtonName: 'Try Community',
    chiliPiper: CHILI_PIPER_FORMS_MAP.engage.community,
    product: PRODUCT_MAP.ENGAGE,
    popupName: 'startTrial',
  },
  listeningOverview: {
    bannerButtonName: 'Try Social Listening',
    chiliPiper: CHILI_PIPER_FORMS_MAP.socialListening.upgrade,
    product: PRODUCT_MAP.SOCIAL_LISTENING,
    popupName: 'startTrial',
  },
});

const getFacebookOverviewTemplate = () => {
  const flagStore = useFlagStore();
  const useLegacy = !(flagStore.ready && flagStore.flags.facebookMetricsUpdatesDashboards);

  return {
    id: 'facebookOverview',
    name: 'Facebook Overview',
    description:
      'Measure your most important Facebook metrics and view your content performance across brands.',
    legacyButtonName: 'Facebook Overview',
    bannerButtonName: 'Create Dashboard',
    cardImage: getCdnAssetUrl('dashboards/template-card-channel.svg'),
    previewImage: getCdnAssetUrl('dashboards/template-preview-faceboook.png'),
    bannerSrc: IMG.dashboard.template.facebookOverview,
    bannerSrcset: getSrcSet('dashboards/templates/facebook-overview'),
    layouts: {
      singleBrand: useLegacy
        ? FACEBOOK_OVERVIEW_SINGLE_BRAND_LEGACY
        : FACEBOOK_OVERVIEW_SINGLE_BRAND,
      multiBrand: useLegacy ? FACEBOOK_OVERVIEW_MULTI_BRAND_LEGACY : FACEBOOK_OVERVIEW_MULTI_BRAND,
    },
    channels: ['FACEBOOK'],
    allowContentTags: true,
  };
};

const getTikTokOverviewTemplate = () => ({
  id: 'tikTokOverview',
  name: 'TikTok Overview',
  description:
    'Measure your most important TikTok metrics and view your content performance across brands.',
  legacyButtonName: 'TikTok Overview',
  bannerButtonName: 'Create Dashboard',
  cardImage: getCdnAssetUrl('dashboards/template-card-channel.svg'),
  previewImage: getCdnAssetUrl('dashboards/template-preview-tiktok.png'),
  previewImageOld: getCdnAssetUrl('dashboards/template-preview-tiktok-old.png'),
  layouts: {
    singleBrand: TIKTOK_OVERVIEW_SINGLE_BRAND,
    multiBrand: TIKTOK_OVERVIEW_MULTI_BRAND,
  },
  bannerSrc: IMG.dashboard.template.tiktokOverview,
  bannerSrcset: getSrcSet('dashboards/templates/tiktok-overview'),
  channels: ['TIKTOK'],
  allowContentTags: true,
});

const getBrandHealthTemplate = () => {
  const flagStore = useFlagStore();
  const useLegacy = !(flagStore.ready && flagStore.flags.facebookMetricsUpdatesDashboards);

  return {
    id: 'brandHealth',
    name: 'Brand Health',
    description:
      'Measure how your posts are resonating with your audience by tracking overall sentiment and engagement.',
    legacyButtonName: 'Brand Health',
    bannerButtonName: 'Create Dashboard',
    cardImage: getCdnAssetUrl('dashboards/template-card-channel.svg'),
    previewImage: getCdnAssetUrl('dashboards/template-preview-brandhealth.png'),
    createdAt: 'April 24 2023 00:00:01 UTC',
    layouts: {
      singleBrand: useLegacy ? BRAND_HEALTH_SINGLE_BRAND_LEGACY : BRAND_HEALTH_SINGLE_BRAND,
      multiBrand: useLegacy ? BRAND_HEALTH_MULTI_BRAND_LEGACY : BRAND_HEALTH_MULTI_BRAND,
    },
    bannerSrc: IMG.dashboard.template.brandHealth,
    bannerSrcset: getSrcSet('dashboards/templates/brand-health-overview'),
    iconOverrideList: [{ value: 'brandhealth', competitive: false }],
    brandPermissions: [BRAND.GENERAL.CAN_ACCESS_SENTIMENT],
    brandDisabledTooltip: 'This template is not included in this brand’s plan.',
    showUpgrade: true,
    channels: ['FACEBOOK', 'INSTAGRAM', 'TIKTOK', 'TWITTER', 'YOUTUBE'],
    upgradeOptions: TEMPLATE_UPGRADE_MAP.brandHealth,
    allowContentTags: true,
  };
};

export const TEMPLATES = Object.freeze({
  blank: {
    id: 'blank',
    name: 'Blank Dashboard',
    description:
      'Build your Dashboard from scratch. Add the metrics, channels, and brands that matter most to your team.',
    legacyButtonName: 'Blank Dashboard',
    bannerButtonName: 'Create Dashboard',
    cardImage: getCdnAssetUrl('dashboards/template-card-blank.svg'),
    previewImage: getCdnAssetUrl('dashboards/template-preview-blank.png'),
    bannerSrc: IMG.dashboard.template.blank,
    bannerSrcset: getSrcSet('dashboards/templates/blank-overview'),
    channels: [],
    allowContentTags: false,
  },
  socialOverview: {
    id: 'socialOverview',
    name: '360 Social Overview',
    description: 'Get a holistic view of your social strategy across your most important channels.',
    legacyButtonName: '360 Social Overview',
    bannerButtonName: 'Create Dashboard',
    cardImage: getCdnAssetUrl('dashboards/template-card-socialoverview.png'),
    previewImage: getCdnAssetUrl('dashboards/template-preview-360.png'),
    layouts: {
      singleBrand: SOCIAL_OVERVIEW_SINGLE_BRAND,
    },
    bannerSrc: IMG.dashboard.template.socialOverview,
    bannerSrcset: getSrcSet('dashboards/templates/social-overview'),
    channels: ['FACEBOOK', 'INSTAGRAM', 'LINKEDIN', 'PINTEREST', 'TIKTOK', 'TWITTER', 'YOUTUBE'],
    allowContentTags: true,
  },
  competitiveOverview: {
    id: 'competitiveOverview',
    name: 'Competitive Overview',
    description:
      'Track your performance against key competitors and the average of your competitive set.',
    legacyButtonName: 'Competitive',
    bannerButtonName: 'Create Dashboard',
    cardImage: getCdnAssetUrl('dashboards/template-card-compoverview.png'),
    previewImage: getCdnAssetUrl('dashboards/template-preview-compoverview.png'),
    bannerSrc: IMG.dashboard.template.competitiveOverview,
    bannerSrcset: getSrcSet('dashboards/templates/social-competitive-overview'),
    createdAt: 'May 10 2022 00:00:01 UTC',
    layouts: {
      multiBrand: COMPETITIVE_OVERVIEW_MULTI_BRAND,
    },
    brandPermissions: [BRAND.COMPETITIVE.CAN_ACCESS_COMPETITORS],
    showUpgrade: true,
    userPermissions: [USER.COMPETITIVE.CAN_ACCESS_COMPETITORS],
    brandDisabledTooltip: 'This template is not included in this brand’s plan.',
    channels: ['INSTAGRAM'],
    upgradeOptions: TEMPLATE_UPGRADE_MAP.competitiveOverview,
    allowContentTags: true,
  },
  get brandHealth() {
    return getBrandHealthTemplate();
  },
  creatorsOverview: {
    id: 'creatorsOverview',
    name: 'Creators Overview',
    description:
      'Measure the performance of your Creators and the impact their posts have on your brands.',
    legacyButtonName: 'Creators Overview',
    bannerButtonName: 'Create Dashboard',
    cardImage: getCdnAssetUrl('dashboards/template-card-channel.svg'),
    previewImage: getCdnAssetUrl('dashboards/template-preview-creators.png'),
    bannerSrc: IMG.dashboard.template.creatorsOverview,
    bannerSrcset: getSrcSet('dashboards/templates/creator-overview'),
    layouts: {
      singleBrand: CREATOR_OVERVIEW_SINGLE_BRAND,
      multiBrand: CREATOR_OVERVIEW_MULTI_BRAND,
      singleCreator: CREATOR_OVERVIEW_SINGLE_CREATOR,
      multiCreator: CREATOR_OVERVIEW_MULTI_CREATOR,
    },
    channels: ['INSTAGRAM', 'TIKTOK'],
    allowContentTags: true,
  },
  communityOverview: {
    id: 'communityOverview',
    name: 'Community Overview',
    description: 'Measure your brand and team performance in Community across your social channels',
    bannerButtonName: 'Create Dashboard',
    bannerSrc: IMG.dashboard.template.communityOverview,
    bannerSrcset: getSrcSet('dashboards/templates/community-overview'),
    channels: ['FACEBOOK', 'INSTAGRAM', 'TIKTOK', 'TWITTER', 'YOUTUBE'],
    allowContentTags: true,
    brandPermissions: [BRAND.COMMUNITY.CAN_ACCESS_COMMUNITY],
    upgradeOptions: TEMPLATE_UPGRADE_MAP.communityOverview,
    showUpgrade: true,
    layouts: {
      singleBrand: COMMUNITY_SINGLE_BRAND,
      multiBrand: COMMUNITY_MULTI_BRAND,
    },
  },
  listeningOverview: {
    id: 'listeningOverview',
    name: 'Social Listening Overview',
    description: 'Measure topic performance across social channels',
    bannerButtonName: 'Create Dashboard',
    bannerSrc: IMG.dashboard.template.listeningOverview,
    bannerSrcset: getSrcSet('dashboards/templates/listening-overview'),
    channels: [
      'FACEBOOK',
      'INSTAGRAM',
      'LINKEDIN',
      'TIKTOK',
      'TWITTER',
      'YOUTUBE',
      'REDDIT',
      'WEB',
    ],
    allowContentTags: false,
    organizationPermissions: [ORGANIZATION.LISTENING.CAN_ACCESS_SOCIAL_LISTENING],
    upgradeOptions: TEMPLATE_UPGRADE_MAP.listeningOverview,
    showUpgrade: true,
    layouts: {
      singleTopic: LISTENING_OVERVIEW_SINGLE_TOPIC,
      multiTopic: LISTENING_OVERVIEW_MULTI_TOPIC,
    },
  },
  get tikTokOverview() {
    return getTikTokOverviewTemplate();
  },
  instagramOverview: {
    id: 'instagramOverview',
    name: 'Instagram Overview',
    description:
      'Measure your most important Instagram metrics and view your content performance across brands.',
    legacyButtonName: 'Instagram Overview',
    bannerButtonName: 'Create Dashboard',
    cardImage: getCdnAssetUrl('dashboards/template-card-channel.svg'),
    previewImage: getCdnAssetUrl('dashboards/template-preview-instagram.png'),
    bannerSrc: IMG.dashboard.template.instagramOverview,
    bannerSrcset: getSrcSet('dashboards/templates/instagram-overview'),
    layouts: {
      singleBrand: INSTAGRAM_OVERVIEW_SINGLE_BRAND,
      multiBrand: INSTAGRAM_OVERVIEW_MULTI_BRAND,
    },
    channels: ['INSTAGRAM'],
    allowContentTags: true,
  },
  get facebookOverview() {
    return getFacebookOverviewTemplate();
  },
  pinterestOverview: {
    id: 'pinterestOverview',
    name: 'Pinterest Overview',
    description:
      'Measure your most important Pinterest metrics and view your content performance across brands.',
    legacyButtonName: 'Pinterest Overview',
    bannerButtonName: 'Create Dashboard',
    cardImage: getCdnAssetUrl('dashboards/template-card-channel.svg'),
    previewImage: getCdnAssetUrl('dashboards/template-preview-pinterest.png'),
    bannerSrc: IMG.dashboard.template.pinterestOverview,
    bannerSrcset: getSrcSet('dashboards/templates/pinterest-overview'),
    layouts: {
      singleBrand: PINTEREST_OVERVIEW_SINGLE_BRAND,
      multiBrand: PINTEREST_OVERVIEW_MULTI_BRAND,
    },
    channels: ['PINTEREST'],
    allowContentTags: true,
  },
  twitterOverview: {
    id: 'twitterOverview',
    name: 'X Overview',
    description:
      'Measure your most important X metrics and view your content performance across brands.',
    legacyButtonName: 'X Overview',
    bannerButtonName: 'Create Dashboard',
    cardImage: getCdnAssetUrl('dashboards/template-card-channel.svg'),
    previewImage: getCdnAssetUrl('dashboards/template-preview-twitter.png'),
    bannerSrc: IMG.dashboard.template.xOverview,
    bannerSrcset: getSrcSet('dashboards/templates/x-overview'),
    layouts: {
      singleBrand: TWITTER_OVERVIEW_SINGLE_BRAND,
      multiBrand: TWITTER_OVERVIEW_MULTI_BRAND,
    },
    channels: ['TWITTER'],
    allowContentTags: true,
  },
  youtubeOverview: {
    id: 'youtubeOverview',
    name: 'YouTube Overview',
    description:
      'Measure your most important YouTube metrics and view your content performance across brands.',
    legacyButtonName: 'YouTube Overview',
    bannerButtonName: 'Create Dashboard',
    cardImage: getCdnAssetUrl('dashboards/template-card-channel.svg'),
    previewImage: getCdnAssetUrl('dashboards/template-preview-youtube.png'),
    bannerSrc: IMG.dashboard.template.youtubeOverview,
    bannerSrcset: getSrcSet('dashboards/templates/youtube-overview'),
    createdAt: 'May 17 2023 00:00:01 UTC',
    layouts: {
      singleBrand: YOUTUBE_OVERVIEW_SINGLE_BRAND,
      multiBrand: YOUTUBE_OVERVIEW_MULTI_BRAND,
    },
    channels: ['YOUTUBE'],
  },
  linkedinOverview: {
    id: 'linkedinOverview',
    name: 'LinkedIn Overview',
    description:
      'Measure your most important LinkedIn metrics and view your content performance across brands.',
    legacyButtonName: 'LinkedIn Overview',
    bannerButtonName: 'Create Dashboard',
    cardImage: getCdnAssetUrl('dashboards/template-card-channel.svg'),
    previewImage: getCdnAssetUrl('dashboards/template-preview-linkedin.png'),
    bannerSrc: IMG.dashboard.template.linkedinOverview,
    bannerSrcset: getSrcSet('dashboards/templates/linkedin-overview'),
    createdAt: 'June 3 2024 00:00:01 UTC',
    layouts: {
      singleBrand: LINKEDIN_OVERVIEW_SINGLE_BRAND,
      multiBrand: LINKEDIN_OVERVIEW_MULTI_BRAND,
    },
    channels: ['LINKEDIN'],
    allowContentTags: true,
  },
});

export const DASHBOARD_TEMPLATE_OVERVIEW = Object.freeze({
  bannerSrc: IMG.dashboard.template.dashboardOverview,
  bannerSrcset: getSrcSet('dashboards/templates/dashboard-overview'),
});
